import { useCallback, useMemo } from 'react';
import type { PersonNameChildAttribute, PersonNameFieldKey, PersonNameValue } from './types';

/**
 * 氏名フィールドの表示順序
 */
const FIELD_ORDER: PersonNameFieldKey[] = ['familyName', 'givenName', 'middleName', 'previousFamilyName', 'familyNameKana', 'givenNameKana', 'middleNameKana', 'previousFamilyNameKana', 'familyNameEn', 'givenNameEn', 'middleNameEn', 'previousFamilyNameEn'];

/**
 * childAttributesからフィールドを取得するヘルパー
 */
function getChildByFieldKey(children: PersonNameChildAttribute[], fieldKey: PersonNameFieldKey): PersonNameChildAttribute | undefined {
  return children.find(c => c.fieldKey === fieldKey);
}
type UsePersonNameOptions = {
  /** 氏名の値 */
  value: PersonNameValue;
  /** 値変更時のコールバック */
  onChange: (value: PersonNameValue) => void;
  /** 子属性（各フィールドのname/required/readonlyを個別に指定） */
  childAttributes?: PersonNameChildAttribute[];
  /** コンポーネント全体のreadonlyフラグ */
  readonly?: boolean;
};

/**
 * 氏名入力のロジックを管理するフック
 *
 * 主な責務:
 * - フィールド設定の解決（childAttributesとデフォルト値のマージ）
 * - 値の更新ハンドラ
 * - 表示用のフォーマット済み値
 */
export const usePersonName = ({
  value,
  onChange,
  childAttributes = [],
  readonly = false
}: UsePersonNameOptions) => {
  // 名前フィールド（姓・名・ミドルネーム・旧姓）の設定を取得
  const nameFields = useMemo(() => {
    const familyNameChild = getChildByFieldKey(childAttributes, 'familyName');
    const givenNameChild = getChildByFieldKey(childAttributes, 'givenName');
    const middleNameChild = getChildByFieldKey(childAttributes, 'middleName');
    const previousFamilyNameChild = getChildByFieldKey(childAttributes, 'previousFamilyName');
    return [familyNameChild, givenNameChild, middleNameChild, previousFamilyNameChild].filter((c): c is PersonNameChildAttribute => c !== undefined);
  }, [childAttributes]);

  // カナフィールド（姓カナ・名カナ・ミドルネームカナ・旧姓カナ）の設定を取得
  const kanaFields = useMemo(() => {
    const familyNameKanaChild = getChildByFieldKey(childAttributes, 'familyNameKana');
    const givenNameKanaChild = getChildByFieldKey(childAttributes, 'givenNameKana');
    const middleNameKanaChild = getChildByFieldKey(childAttributes, 'middleNameKana');
    const previousFamilyNameKanaChild = getChildByFieldKey(childAttributes, 'previousFamilyNameKana');
    return [familyNameKanaChild, givenNameKanaChild, middleNameKanaChild, previousFamilyNameKanaChild].filter((c): c is PersonNameChildAttribute => c !== undefined);
  }, [childAttributes]);

  // 英語名フィールド（姓En・名En・ミドルネームEn・旧姓En）の設定を取得
  const enFields = useMemo(() => {
    const familyNameEnChild = getChildByFieldKey(childAttributes, 'familyNameEn');
    const givenNameEnChild = getChildByFieldKey(childAttributes, 'givenNameEn');
    const middleNameEnChild = getChildByFieldKey(childAttributes, 'middleNameEn');
    const previousFamilyNameEnChild = getChildByFieldKey(childAttributes, 'previousFamilyNameEn');
    return [familyNameEnChild, givenNameEnChild, middleNameEnChild, previousFamilyNameEnChild].filter((c): c is PersonNameChildAttribute => c !== undefined);
  }, [childAttributes]);

  // 全フィールドの設定（順序保証）
  const allFields = useMemo(() => {
    return FIELD_ORDER.map(fieldKey => getChildByFieldKey(childAttributes, fieldKey)).filter((c): c is PersonNameChildAttribute => c !== undefined);
  }, [childAttributes]);

  // フィールドがreadonlyかどうかを判定
  const isFieldReadonly = useCallback((child: PersonNameChildAttribute) => {
    return child.readonly || readonly;
  }, [readonly]);

  // 単一フィールドの値を更新
  const handleFieldChange = useCallback((fieldKey: PersonNameFieldKey, newValue: string) => {
    onChange({
      ...value,
      [fieldKey]: newValue
    });
  }, [value, onChange]);

  // Japanese order: familyName → middleName → givenName
  const displayName = useMemo(() => {
    return [value.familyName, value.middleName, value.givenName].filter(Boolean).join(' ');
  }, [value.familyName, value.middleName, value.givenName]);

  // Japanese kana order: familyNameKana → middleNameKana → givenNameKana
  const displayNameKana = useMemo(() => {
    return [value.familyNameKana, value.middleNameKana, value.givenNameKana].filter(Boolean).join(' ');
  }, [value.familyNameKana, value.middleNameKana, value.givenNameKana]);

  // Western order: givenNameEn → middleNameEn → familyNameEn
  const displayNameEn = useMemo(() => {
    return [value.givenNameEn, value.middleNameEn, value.familyNameEn].filter(Boolean).join(' ');
  }, [value.givenNameEn, value.middleNameEn, value.familyNameEn]);
  const displayPreviousName = value.previousFamilyName || '';
  const displayPreviousNameKana = value.previousFamilyNameKana || '';
  const displayPreviousNameEn = value.previousFamilyNameEn || '';
  return {
    /** 名前フィールド（姓・名・ミドルネーム・旧姓）の設定 */
    nameFields,
    /** カナフィールド（姓カナ・名カナ・ミドルネームカナ・旧姓カナ）の設定 */
    kanaFields,
    /** 英語名フィールド（姓En・名En・ミドルネームEn・旧姓En）の設定 */
    enFields,
    /** 全フィールドの設定（順序保証） */
    allFields,
    /** フィールドがreadonlyかどうかを判定 */
    isFieldReadonly,
    /** 単一フィールドの値を更新 */
    handleFieldChange,
    /** 表示用のフォーマット済み氏名 */
    displayName,
    /** 表示用のフォーマット済みカナ */
    displayNameKana,
    /** 表示用のフォーマット済み英語名 */
    displayNameEn,
    /** 表示用の旧姓 */
    displayPreviousName,
    /** 表示用の旧姓カナ */
    displayPreviousNameKana,
    /** 表示用の旧姓英語 */
    displayPreviousNameEn
  };
};